/**
 * Zod Schema Validation for Timeline Serialization
 *
 * These schemas provide runtime validation for Timeline state objects,
 * ensuring data integrity during serialization/deserialization.
 */

import { z } from 'zod';
import { MEDIA_ROLES, normalizeMediaRole } from '../MediaRole';

const TRANSITION_TYPES = [
  'smart',
  'none',
  'fade',
  'crossfade',
  'cross-zoom',
  'wipe',
  'wipe-up',
  'wipe-down',
  'wipe-left',
  'wipe-right',
  'slide',
  'slide-up',
  'slide-down',
  'slide-left',
  'slide-right'
] as const;

const TransitionTypeSchema = z.enum(TRANSITION_TYPES);
const TransitionDirectionSchema = z.enum(['up', 'down', 'left', 'right']);
const TransitionModeSchema = z.union([
  z.enum(['prefix', 'postfix', 'overlay']),
  z.literal('overlap').transform(() => 'overlay' as const)
]);
const MediaRoleSchema = z.union([
  z.enum(MEDIA_ROLES),
  z.string().transform(value => normalizeMediaRole(value))
]);

/**
 * Base schema for all serializable states
 */
const SerializableStateSchema = z.object({
  type: z.string(),
  version: z.string()
});

/**
 * Grid area schema
 */
const GridAreaSchema = z.object({
  row: z.coerce.number().min(1),
  col: z.coerce.number().min(1),
  rows: z.coerce.number().min(1),
  cols: z.coerce.number().min(1),
  gridSize: z.coerce.number().min(1)
});

/**
 * Animation timing schema
 */
const AnimationTimingSchema = z.object({
  duration: z.coerce.number().positive(),
  delay: z.coerce.number().optional(),
  easing: z.string().optional()
});

/**
 * Animation schema
 */
const AnimationSchema = z.object({
  type: z.string(),
  timing: AnimationTimingSchema,
  timingMode: z.enum(['before-at', 'at-after', 'centered']).optional(),
  direction: z.string().optional(),
  scale: z.coerce.number().optional()
});

/**
 * Transition schema
 * Note: duration can be 0 for 'none' type transitions
 */
const TransitionSchema = z.object({
  type: TransitionTypeSchema,
  duration: z.coerce.number().min(0), // Allow 0 for 'none' transitions
  mode: TransitionModeSchema.optional(),
  direction: TransitionDirectionSchema.optional(),
  easing: z.string().optional()
});

/**
 * Text style schema
 */
const TextStyleSchema = z.object({
  primaryColor: z.string().optional(),
  currentSpokenWordColor: z.string().optional(),
  fontFamily: z.string().optional(),
  maxFontSize: z.coerce.number().positive().optional(),
  outline: z.string().optional(),
  background: z.string().optional(),
  textAnimation: z.string().optional()
}).strict();

const EffectObjectSchema = z.object({
  type: z.string()
}).passthrough();
const EffectStateSchema = z.union([
  EffectObjectSchema,
  z.string().transform((type): { [key: string]: unknown; type: string } => ({ type }))
]);

/**
 * Base media schema
 */
const BaseMediaSchema = SerializableStateSchema.extend({
  id: z.string(),
  role: MediaRoleSchema.optional()
});

/**
 * Crop box schema
 */
const CropBoxSchema = z.object({
  x: z.coerce.number(),
  y: z.coerce.number(),
  width: z.coerce.number().positive(),
  height: z.coerce.number().positive()
}).optional();

/**
 * Media type normalization helper.
 * Accepts both serialization type names ('VideoMedia', 'ImageMedia', etc.)
 * and runtime MediaSourceType values ('video', 'image', etc.) for backward
 * compatibility, then normalizes to the canonical serialization form.
 */
function mediaTypeEnum<S extends string, R extends string>(serialized: S, runtime: R) {
  return z.union([z.literal(serialized), z.literal(runtime)]).transform(() => serialized as S);
}

/**
 * Video media schema
 * Accepts type 'VideoMedia' (serialization) or 'video' (runtime MediaSourceType)
 */
export const VideoMediaSchema = BaseMediaSchema.extend({
  type: mediaTypeEnum('VideoMedia', 'video'),
  version: z.literal('1.0.0'),
  src: z.string(),
  offset: z.coerce.number().optional(),
  duration: z.coerce.number().positive().optional(),
  volume: z.coerce.number().min(0).max(1).optional(),
  muted: z.coerce.boolean().optional(),
  transparencyMode: z.enum(['none', 'alpha', 'screen']).optional(),
  effects: z.array(EffectStateSchema).optional(),
  crop: CropBoxSchema,
  usePartialDownload: z.coerce.boolean().optional()
}).strict();

/**
 * Image media schema
 * Accepts type 'ImageMedia' (serialization) or 'image' (runtime MediaSourceType)
 */
export const ImageMediaSchema = BaseMediaSchema.extend({
  type: mediaTypeEnum('ImageMedia', 'image'),
  version: z.literal('1.0.0'),
  src: z.string(),
  duration: z.coerce.number().positive().optional(),
  effects: z.array(EffectStateSchema).optional(),
  crop: CropBoxSchema
}).strict();

/**
 * Text media schema
 * Accepts type 'TextMedia' (serialization) or 'text' (runtime MediaSourceType)
 */
export const TextMediaSchema = BaseMediaSchema.extend({
  type: mediaTypeEnum('TextMedia', 'text'),
  version: z.literal('1.0.0'),
  text: z.string(),
  content: z.record(z.unknown())
}).strict();

/**
 * Generated slide schema
 * Accepts type 'GeneratedSlide' (serialization) or 'generated' (runtime MediaSourceType)
 */
export const GeneratedSlideSchema = BaseMediaSchema.extend({
  type: mediaTypeEnum('GeneratedSlide', 'generated'),
  version: z.literal('1.0.0'),
  content: z.record(z.unknown()),
  effects: z.array(EffectStateSchema).optional()
}).strict();

/**
 * Media schema union
 * Uses z.union instead of z.discriminatedUnion because the type field
 * now accepts multiple values per schema (both serialization and runtime names).
 */
const MediaSchema = z.union([
  VideoMediaSchema,
  ImageMediaSchema,
  TextMediaSchema,
  GeneratedSlideSchema
]);

/**
 * Scene schema
 */
export const SceneSchema = SerializableStateSchema.extend({
  type: z.literal('Scene'),
  version: z.literal('1.0.0'),
  id: z.string(),
  startAt: z.coerce.number().min(0),
  duration: z.coerce.number().positive(),
  layout: z.string(),
  objectFit: z.string().optional(),
  inputs: z.array(MediaSchema),
  transitionIn: TransitionSchema.optional(),
  transitionOut: TransitionSchema.optional()
}).strict();

/**
 * Token schema for captions
 */
const TokenSchema = z.object({
  text: z.string(),
  fromMs: z.coerce.number().optional(),
  toMs: z.coerce.number().optional(),
  confidence: z.coerce.number().min(0).max(1).optional()
}).strict();

/**
 * Overlay schema
 */
export const OverlaySchema = SerializableStateSchema.extend({
  type: z.literal('Overlay'),
  version: z.literal('1.0.0'),
  id: z.string(),
  startAt: z.coerce.number().min(0),
  duration: z.coerce.number().positive(),
  mediaObject: MediaSchema,
  position: z.string().optional(),
  gridArea: GridAreaSchema.optional(),
  entranceAnimation: AnimationSchema.optional(),
  exitAnimation: AnimationSchema.optional()
}).strict();

/**
 * Text overlay schema
 */
export const TextOverlaySchema = SerializableStateSchema.extend({
  type: z.literal('TextOverlay'),
  version: z.literal('1.0.0'),
  id: z.string(),
  startAt: z.coerce.number().min(0),
  duration: z.coerce.number().positive(),
  text: z.string(),
  position: z.string().optional(),
  gridArea: GridAreaSchema.optional(),
  textStyle: TextStyleSchema.optional(),
  entranceAnimation: AnimationSchema.optional(),
  exitAnimation: AnimationSchema.optional()
}).strict();

/**
 * Caption schema
 */
export const CaptionSchema = SerializableStateSchema.extend({
  type: z.literal('Caption'),
  version: z.literal('1.0.0'),
  id: z.string(),
  startAt: z.coerce.number().min(0),
  duration: z.coerce.number().positive(),
  tokens: z.array(TokenSchema),
  position: z.string().optional(),
  textStyle: TextStyleSchema.optional()
}).strict();

/**
 * Branding schema
 */
export const BrandingSchema = SerializableStateSchema.extend({
  type: z.literal('Branding'),
  version: z.literal('1.0.0'),
  id: z.string(),
  startAt: z.coerce.number().min(0),
  duration: z.coerce.number().positive(),
  mediaObject: MediaSchema,
  position: z.string().optional(),
  gridArea: GridAreaSchema.optional()
}).strict();

/**
 * Timeline settings schema
 */
const TimelineSettingsSchema = z.object({
  width: z.coerce.number().int().positive(),
  height: z.coerce.number().int().positive(),
  durationInFrames: z.coerce.number().int().min(1),
  backgroundColor: z.string()
}).strict();

/**
 * Timeline defaults schema
 */
const TimelineDefaultsSchema = z.object({
  layoutPadding: z.coerce.number().optional(),
  transitionIn: TransitionSchema.optional(),
  transitionOut: TransitionSchema.optional(),
  premountFrames: z.coerce.number().optional()
}).strict();

/**
 * Main audio schema - primary audio track for the timeline
 */
const MainAudioSchema = z.object({
  audioUrl: z.string().url(),
  duration: z.coerce.number().positive(),
  volume: z.coerce.number().min(0).max(1).optional()
}).strict();

/**
 * Main Timeline schema
 */
export const TimelineSchema = SerializableStateSchema.extend({
  type: z.literal('Timeline'),
  version: z.literal('1.0.0'),
  fps: z.coerce.number().positive(),
  aspectRatio: z.string(),
  settings: TimelineSettingsSchema,
  defaults: TimelineDefaultsSchema.optional(),
  scenes: z.array(SceneSchema),
  overlays: z.array(OverlaySchema).default([]),
  textOverlays: z.array(TextOverlaySchema).default([]),
  captions: z.array(CaptionSchema).default([]),
  branding: z.array(BrandingSchema).default([]),
  mainAudio: MainAudioSchema.optional()
}).strict();

/**
 * Validate Timeline state
 * @param state - The state to validate
 * @returns Validated state or throws ZodError
 */
export function validateTimelineState(state: unknown): z.infer<typeof TimelineSchema> {
  return TimelineSchema.parse(state);
}

/**
 * Safe validate Timeline state
 * @param state - The state to validate
 * @returns Success object with data or error
 */
export function safeValidateTimelineState(state: unknown) {
  return TimelineSchema.safeParse(state);
}

/**
 * Validate Scene state
 */
export function validateSceneState(state: unknown): z.infer<typeof SceneSchema> {
  return SceneSchema.parse(state);
}

/**
 * Validate Media state
 */
export function validateMediaState(state: unknown): z.infer<typeof MediaSchema> {
  return MediaSchema.parse(state);
}

/**
 * Create type-safe Timeline state
 */
export type TimelineStateType = z.infer<typeof TimelineSchema>;
export type SceneStateType = z.infer<typeof SceneSchema>;
export type VideoMediaStateType = z.infer<typeof VideoMediaSchema>;
export type ImageMediaStateType = z.infer<typeof ImageMediaSchema>;
export type TextMediaStateType = z.infer<typeof TextMediaSchema>;
export type GeneratedSlideStateType = z.infer<typeof GeneratedSlideSchema>;

/**
 * Utility function to strip unknown properties
 */
export function cleanTimelineState(state: unknown): TimelineStateType {
  const result = TimelineSchema.safeParse(state);
  if (result.success) {
    return result.data;
  } else {
    console.error('[Timeline Schema] Validation errors:', result.error.errors);
    throw new Error(`Invalid Timeline state: ${result.error.message}`);
  }
}

/**
 * Validate partial updates (for incremental serialization)
 */
export const PartialTimelineSchema = TimelineSchema.partial({
  scenes: true,
  overlays: true,
  textOverlays: true,
  captions: true,
  branding: true
});

/**
 * Export all schemas for testing
 */
export const schemas = {
  Timeline: TimelineSchema,
  Scene: SceneSchema,
  VideoMedia: VideoMediaSchema,
  ImageMedia: ImageMediaSchema,
  TextMedia: TextMediaSchema,
  GeneratedSlide: GeneratedSlideSchema,
  Overlay: OverlaySchema,
  TextOverlay: TextOverlaySchema,
  Caption: CaptionSchema,
  Branding: BrandingSchema
};
